import { useCallback, useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Copy, FileText, MailCheck } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Frame } from "@/pages/PasswordReset";
import { KeyRotation } from "@/pages/OwnerKey";

const inviteLink = (code) => `${window.location.origin}/register?invite=${code}`;

export function OwnerVerify() {
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [state, setState] = useState("working");
  const [error, setError] = useState("");
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    api.post("/owner/verify", { token: params.get("token") || "" })
      .then(async () => { await refresh(); setState("ok"); toast.success("Owner access is on."); nav("/you/owner", { replace: true }); })
      .catch((e) => { setError(errMsg(e)); setState("error"); });
  }, [params, refresh, nav]);
  return (
    <Frame title="Owner access" sub={state === "working" ? "Confirming…" : ""} testid="owner-verify-page">
      {state === "error" && <p data-testid="owner-verify-error" className="text-sm text-red-700 bg-red-50 rounded-lg px-3 py-2">{error}</p>}
    </Frame>
  );
}

function VerifyCard() {
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { await api.post("/owner/verify-request"); setSent(true); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <section className="card p-5" data-testid="owner-verify-card">
      <h2 className="font-display text-xl">Verify owner access</h2>
      <p className="text-sm text-stone-600 mt-1">We'll email a one-time link to your address. Open it while signed in here to turn on full access and the owner tools.</p>
      {sent ? <p className="mt-4 text-sm flex items-center gap-2 text-stone-700" data-testid="owner-verify-sent"><MailCheck className="h-4 w-4" />Check your inbox. The link works for 30 minutes.</p>
        : <button onClick={send} disabled={busy} data-testid="owner-verify-request-btn" className="btn-ink mt-4 px-5 py-2.5 rounded-full text-sm font-semibold disabled:opacity-60">{busy ? "Sending…" : "Send verification email"}</button>}
    </section>
  );
}

function NewInvite({ onCreated }) {
  const [note, setNote] = useState("");
  const [uses, setUses] = useState(1);
  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/owner/invites", { note, max_uses: Number(uses) });
      setNote("");
      await navigator.clipboard?.writeText(inviteLink(data.code)).catch(() => {});
      toast.success(`Invite ${data.code} created and link copied`);
      onCreated();
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={create} className="flex flex-wrap gap-2 items-end" data-testid="owner-new-invite-form">
      <label className="flex-1 min-w-[10rem] text-xs text-stone-600">Note (who it's for)
        <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={120} data-testid="owner-invite-note-input" className="mt-1 w-full h-10 px-3 rounded-lg border border-stone-200 text-sm" /></label>
      <label className="w-20 text-xs text-stone-600">Uses
        <input type="number" min={1} max={500} value={uses} onChange={(e) => setUses(e.target.value)} data-testid="owner-invite-uses-input" className="mt-1 w-full h-10 px-3 rounded-lg border border-stone-200 text-sm" /></label>
      <button data-testid="owner-invite-create-btn" className="btn-ink h-10 px-4 rounded-full text-sm font-semibold">Create code</button>
    </form>
  );
}

function InviteRow({ inv, onChange }) {
  const off = async () => { await api.post(`/owner/invites/${inv.code}/deactivate`); onChange(); };
  const copy = () => navigator.clipboard?.writeText(inviteLink(inv.code)).then(() => toast.success("Invite link copied")).catch((e) => console.error(e));
  return (
    <li className="py-3 flex items-center gap-3 text-sm" data-testid={`owner-invite-${inv.code}`}>
      <span className="flex-1 min-w-0"><span className="font-mono font-semibold">{inv.code}</span>
        <span className="block text-xs text-stone-500 truncate">{inv.note || "No note"} · {inv.uses}/{inv.max_uses} used{inv.active ? "" : " · off"}</span></span>
      <button onClick={copy} aria-label="Copy invite link" data-testid={`owner-invite-copy-${inv.code}`} className="p-2 rounded-full hover:bg-stone-100"><Copy className="h-4 w-4" /></button>
      {inv.active && <button onClick={off} data-testid={`owner-invite-off-${inv.code}`} className="text-xs underline text-stone-600">Turn off</button>}
    </li>
  );
}

function WaitRow({ w, onChange }) {
  const [busy, setBusy] = useState(false);
  const invite = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/owner/waitlist/${w.id}/invite`); toast.success(`Invite ${data.code} emailed to ${w.email}`); onChange(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const remove = async () => { await api.delete(`/owner/waitlist/${w.id}`); onChange(); };
  return (
    <li className="py-3 flex items-center gap-3 text-sm" data-testid={`owner-wait-${w.id}`}>
      <span className="flex-1 min-w-0"><span className="block truncate font-medium">{w.name || w.email}</span>
        <span className="block text-xs text-stone-500 truncate">{w.email}{w.region ? ` · ${w.region}` : ""} · {w.status} · {w.created_at.slice(0, 10)}</span></span>
      {w.status === "waiting" && <button onClick={invite} disabled={busy} data-testid={`owner-wait-invite-${w.id}`} className="btn-ink px-3 py-1.5 rounded-full text-xs font-semibold disabled:opacity-60">{busy ? "Sending…" : "Send invite"}</button>}
      <button onClick={remove} data-testid={`owner-wait-remove-${w.id}`} className="text-xs underline text-stone-600">Remove</button>
    </li>
  );
}

function OwnerTools() {
  const [invites, setInvites] = useState([]);
  const [wait, setWait] = useState([]);
  const load = useCallback(async () => {
    const [a, b] = await Promise.all([api.get("/owner/invites"), api.get("/owner/waitlist")]);
    setInvites(a.data);
    setWait(b.data);
  }, []);
  useEffect(() => { load().catch((e) => toast.error(errMsg(e))); }, [load]);
  return (
    <>
      <section className="card p-5 space-y-4" data-testid="owner-invites">
        <h2 className="font-display text-xl">Invite codes</h2>
        <NewInvite onCreated={load} />
        <ul className="divide-y divide-stone-100">{invites.map((i) => <InviteRow key={i.code} inv={i} onChange={load} />)}</ul>
        {!invites.length && <p className="text-sm text-stone-500" data-testid="owner-invites-empty">No codes yet.</p>}
      </section>
      <section className="card p-5" data-testid="owner-waitlist">
        <h2 className="font-display text-xl">Waitlist <span className="text-stone-400 text-base" data-testid="owner-waitlist-count">{wait.filter((w) => w.status === "waiting").length} waiting</span></h2>
        <ul className="divide-y divide-stone-100 mt-2">{wait.map((w) => <WaitRow key={w.id} w={w} onChange={load} />)}</ul>
        {!wait.length && <p className="text-sm text-stone-500 mt-2" data-testid="owner-waitlist-empty">Nobody on the waitlist yet.</p>}
      </section>
    </>
  );
}

export function OwnerOnly({ children }) {
  const { user } = useAuth();
  return user.owner ? children : <Navigate to="/you" replace />;
}

function ChatQuality() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/owner/chat-metrics").then(({ data }) => setD(data)).catch(() => setD(false)); }, []);
  if (!d) return null;
  const rows = [["Replies", d.replies], ["Average length", `${d.avg_words} words`], ["More than one question", `${d.over_one_question_pct}%`],
    ["Used a banned phrase", d.banned_phrase_replies], ["Thumbs up / down", `${d.thumbs_up} / ${d.thumbs_down}`],
    ["Down: too long / robotic / not helpful / wrong", `${d.down_reasons.too_long} / ${d.down_reasons.robotic} / ${d.down_reasons.not_helpful} / ${d.down_reasons.wrong}`],
    ["Members who chose short / detailed", `${d.short_pref_members} / ${d.detailed_pref_members}`]];
  return (
    <section className="card p-5" data-testid="owner-chat-quality">
      <h2 className="font-display text-xl">Chat quality · last {d.days} days</h2>
      <p className="text-xs text-stone-500 mt-1">Counts only. No member messages are shown.</p>
      <dl className="mt-3 divide-y divide-stone-100 text-sm">
        {rows.map(([k, v]) => <div key={k} className="flex justify-between gap-3 py-2"><dt className="text-stone-600">{k}</dt><dd className="font-semibold text-right" data-testid={`owner-metric-${k.split(" ")[0].toLowerCase()}`}>{v}</dd></div>)}
      </dl>
    </section>
  );
}

export function OwnerConsole() {
  const { user } = useAuth();
  return (
    <div className="grain min-h-screen">
      <div className="relative z-10 max-w-2xl mx-auto px-5 py-10 space-y-5" data-testid="owner-console">
        <Link to="/you" data-testid="owner-back-link" className="inline-flex items-center gap-2 text-sm text-stone-600"><ArrowLeft className="h-4 w-4" />You</Link>
        <h1 className="font-display text-3xl sm:text-4xl tracking-tight">Owner console</h1>
        {user.owner && <Link to="/review" data-testid="owner-review-link" className="card p-4 flex items-center gap-3 text-sm font-semibold"><FileText className="h-4 w-4" />Product review report</Link>}
        {user.owner && <KeyRotation />}
        {user.owner && <ChatQuality />}
        {user.owner ? <OwnerTools /> : user.owner_eligible ? <VerifyCard /> :
          <p className="text-sm text-stone-600" data-testid="owner-not-allowed">This area is only for the CAMRYN owner.</p>}
      </div>
    </div>
  );
}
